// HACK: Phase4Step6 評価実施中の対応となるため、抑制コードを追加
/* eslint-disable max-lines */
/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import config from '../../../../share/config';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import util from '../../../../share/util/utils';

/**
 * 各フェーズ定義
 */
const PHASE = {
  START: 'start',
  CONFIRMATION: 'confirmation',
  EXECUTE: 'execute',
  FL_RR_VALVE_CHECK: 'flRrValveCheck',
  FR_RL_VALVE_CHECK: 'frRlValveCheck',
  SUCCESS: 'success',
  RETRY: 'retry',
  CONFIRMATION_OF_STEP: 'confirmationOfStep',
  OVER_SPEED: 'overSpeed',
  UNABLE_TO_START: 'unableToStart',
  UNABLE_TO_END: 'unableToEnd',
  FAILED: 'failed'
};

/**
 * 待ち時間定義
 */
const WAIT_TIME = {
  TIME_1000: 1000,
  TIME_8000: 8000
};


export default {
  name: 'requirement110',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      timer: null,
      isFirst: true,
      modal_requirement_110: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.OVER_SPEED:
        case PHASE.UNABLE_TO_START:
        case PHASE.UNABLE_TO_END:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.EXECUTE:
        case PHASE.CONFIRMATION_OF_STEP:
        case PHASE.RETRY:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /**
     * 初期化処理
     */
    async init() {
      this.modal_requirement_110 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // 待機処理
      this.startProcess();
    },

    /**
     * Wizardを閉じる
     */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_110 = false;
    },

    /**
     * exit処理(要求&取得)
     * @param {boolean} isCommonError 共通エラー遷移の有無
     */
    async exit(isCommonError) {
      try {
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'exit',
            is_first: this.isFirst
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (isCommonError) {
          if (response) {
            // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
            // エラーコールバックが呼ばれた場合は、null になる。
            switch (response.data.status) {
              case config.STATUS_WSP_COMPLETED:
                // 正常終了時、Wizardを閉じる
                this.closeWizard();
                break;
              case config.STATUS_WSP_FAILED:
                // 異常終了時、共通エラーに遷移
                this.showUnexpectedError();
                break;
              default:
                // 該当のエラーコードが存在しない場合、汎用エラー
                this.showUnexpectedError();
                break;
            }
          }
        } else {
          // エラー遷移無：機能終了
          this.closeWizard();
          return;
        }
      } finally {
        this.isLoading = false;
        this.isFirst = false;
      }
    },

    /**
     * その他エラー発生時の表示
     */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result handler.validate で返ってきたバリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * 待機遷移処理
     */
    async startProcess() {
      clearTimeout(this.timer);
      this.timer = null;
      this.phase = PHASE.START;
      // 1000ms経過後確認遷移
      await util.sleep(WAIT_TIME.TIME_1000);
      this.confirmProcess();
    },

    /**
     * 確認遷移処理
     */
    confirmProcess() {
      this.phase = PHASE.CONFIRMATION;
      // 8000ms経過後画面遷移がない場合、タイムアウト時処理
      this.timer = setTimeout(() => this.execTimeout(), WAIT_TIME.TIME_8000);
    },

    /**
     * 実行遷移処理
     */
    async executeProcess() {
      this.phase = PHASE.EXECUTE;
      await this.execute();
    },

    /**
     * FR/RLバルブチェック遷移処理
     * @param {*} time - 経過時間
     */
    async flRrValveCheckProcess(time) {
      this.phase = PHASE.FL_RR_VALVE_CHECK;
      // 指定時間経過後、FR/RLバルブチェックに遷移
      await util.sleep(time);
      this.frRlValveCheckProcess();
    },

    /**
     * FR/RLバルブチェック遷移処理
     */
    frRlValveCheckProcess() {
      this.phase = PHASE.FR_RL_VALVE_CHECK;
      this.executeCheck();
    },

    /**
     * 終了遷移処理
     * @param {*} time - 経過時間
     */
    async successProcess(userData) {
      this.phase = PHASE.SUCCESS;
      // 指定時間経過後、再実行に遷移
      await util.sleep(userData.wait_time1);
      this.retryProcess(userData.wait_time2);
    },

    // 再実行遷移処理
    async retryProcess(time) {
      this.phase = PHASE.RETRY;
      // 指定時間経過後、画面遷移がなければタイムアウト時処理
      this.timer = setTimeout(() => this.execTimeout(), time);
    },

    // 車輪速超過エラー遷移処理
    async overSpeed() {
      this.phase = PHASE.OVER_SPEED;
    },

    /**
     * 開始不可遷移処理
     * @param {*} time - 経過時間
     */
    async unableToStartProcess(time) {
      this.phase = PHASE.UNABLE_TO_START;
      // 経過時間後、タイムアウト時処理
      this.timer = setTimeout(() => this.execTimeout(), time);
    },

    /**
     * 終了不可遷移処理
     * @param {*} time - 経過時間
     */
    async unableToEndProcess(time) {
      this.phase = PHASE.UNABLE_TO_END;
      // 経過時間後、タイムアウト時処理
      this.timer = setTimeout(() => this.execTimeout(), time);
    },

    /**
     * 確認表示画面OK押下処理
     */
    async read() {
      try {
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'read',
            is_first: this.isFirst
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // SSMIDの内容によって表示内容分岐
              if (response.data.user_data.is_check_brake_pedal){
                // SSMIDの値が0505**
                // ブレーキペダル踏み込み確認表示に遷移
                this.phase = PHASE.CONFIRMATION_OF_STEP;
              } else {
                // SSMIDの値が0505**以外
                await this.sessionTransition();
              }
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 該当のエラーコードが存在しない場合、汎用エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
        this.isFirst = false;
      }
    },

    /**
     * SessionTransition処理(要求&取得)
     */
    async sessionTransition() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'session_transition'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // セキュリティアクセス判定(正常終了)に遷移
              await this.secAccess();
              break;
            case config.STATUS_WSP_FAILED:
              // 異常終了
              switch (parseInt(response.data.async_code)) {
                case error.WSP_EXCESSIVE_WHEEL_SPEED_ERROR:
                  // 車輪速超過エラーに遷移
                  this.overSpeed();
                  break;
                default:
                  // 共通エラーに遷移
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              // 該当のエラーコードが存在しない場合、汎用エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * セキュリティアクセス処理
     */
    async secAccess() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'sec_access'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 実行(正常終了)に遷移
              await this.executeProcess();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 該当のエラーコードが存在しない場合、汎用エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 実行処理(要求&取得)
     */
    async execute() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              await util.sleep(response.data.user_data.wait_time1);
              // FR/RLバルブチェックに遷移
              this.flRrValveCheckProcess(response.data.user_data.wait_time2);
              break;
            case config.STATUS_WSP_FAILED:
              // 異常終了
              // 開始不可に遷移
              this.unableToStartProcess(response.data.user_data.wait_time1);
              break;
            default:
              // 該当のエラーコードが存在しない場合、汎用エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 実行監視処理(要求&取得)
     */
    async executeCheck() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'execute_check'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 終了に遷移
              this.successProcess(response.data.user_data);
              break;
            case config.STATUS_WSP_FAILED:
              // 終了不可に遷移
              this.unableToEndProcess(response.data.user_data.wait_time1);
              break;
            default:
              // 該当のエラーコードが存在しない場合、汎用エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** タイムアウト時処理 */
    async execTimeout() {
      this.isLoading = false;
      clearTimeout(this.timer);
      this.timer = null;
      this.exit(true);
    },
  },

  /**
   * DOM作成後実行処理
   */
  async mounted() {
    // 初期化
    this.init();
  },
};